<template>
  <Card>
    <VRow>
      <VCol cols="12">
        <div class="d-flex flex-column align-center justify-center w-100 ga-3">
          <h1 class="text-title-color font-size-18 font-weight-700">Payment details</h1>
          <div v-if="!isCountrySelected" class="text-subtitle-color font-size-14 font-weight-500">
            Click where you live now:
          </div>
        </div>
      </VCol>
      <VCol v-if="!isCountrySelected" cols="12">
        <VRow>
          <template v-for="country of countryList" :key="country.name">
            <VCol cols="12" sm="4">
              <div
                @click="handleSelectCountry(country)"
                class="d-flex flex-column align-center justify-center country-list-container__item"
              >
                <img :src="country.image" :alt="country.name" />
                <div>{{ country.name }}</div>
              </div>
            </VCol>
          </template>
        </VRow>
      </VCol>
    </VRow>
    <template v-if="isCountrySelected">
      <VForm v-if="isStripeLoaded" v-model="formValid" @submit.prevent>
        <VRow>
          <VCol cols="12">
            <VTextField
              label="Email"
              variant="outlined"
              color="primary"
              name="email"
              type="email"
              placeholder="e.g. name@example.com"
              v-model="email"
              :disabled="isPayLoading"
              :rules="formRules.email"
              class="custom-input"
            />
          </VCol>
          <VCol cols="12">
            <div class="text-title-color font-size-16 font-weight-600 mb-3">Personal Details</div>
            <div id="address-element"></div>
          </VCol>
          <VCol cols="12">
            <label>Card Number</label>
            <div id="card-element"></div>
          </VCol>
          <VCol cols="12" class="mt-3">
            <VTextarea
              v-model.trim="howDidYouHearAboutUs"
              label="How did you hear about us?"
              variant="outlined"
              maxlength="255"
              :disabled="isPayLoading"
              class="custom-input"
            />
          </VCol>
          <VCol cols="12">
            <VSelect
              label="Which Agent Did You Speak to?"
              :items="agentsOptions"
              v-model="agent"
              variant="outlined"
              :disabled="isPayLoading"
              class="custom-input"
            />
          </VCol>
          <VCol cols="12">
            <div class="d-flex flex-column align-center justify-center">
              <VCheckbox v-model="isAgreeTermsConditions" :disabled="isPayLoading" :rules="formRules.terms">
                <template v-slot:label>
                  <div>
                    I agree to XtraPension's
                    <a href="https://www.xtrapension.com/terms" target="_blank" @click.stop> Terms & Conditions</a>
                  </div>
                </template>
              </VCheckbox>
            </div>
          </VCol>
          <VCol cols="12">
            <div v-if="isPayLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
              <VProgressCircular indeterminate color="primary" />
            </div>
            <div class="d-flex items-center justify-center w-100">
              <button class="main-btn" :disabled="isPayLoading || !isAgreeTermsConditions" @click="pay()" type="submit">
                Pay {{ currencySymbol }}{{ formatAmount(amount) }}
              </button>
            </div>
            <div v-if="stripeValidationError" class="info-container info-container__red my-2">
              <p>{{ stripeValidationError }}</p>
            </div>
          </VCol>
        </VRow>
      </VForm>
      <div v-else class="d-flex flex-column align-center justify-center py-4 w-100">
        <VProgressCircular indeterminate color="primary" />
      </div>
    </template>
  </Card>
</template>
<script>
import { mapActions } from 'vuex';
import StripeService from '@/services/stripe-service/stripe.service';
import applicationsService from '@/services/applications-service/applications.service';
import Card from '@/components/common/Card/Card.vue';

const EUROPEAN_COUNTRY_CODES = [
  'AL', 'AD', 'AM', 'AT', 'AZ', 'BY', 'BE', 'BA', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GE',
  'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'KZ', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL',
  'PT', 'RO', 'RU', 'RS', 'SE', 'SI', 'SK', 'SM', 'TR', 'UA', 'UK', 'VA', 'GI', 'JE', 'GG'
]; // prettier-ignore

export default {
  name: 'ApplicationAndReapplyPayment',
  inject: ['agentsOptions'],
  components: {
    Card
  },
  emits: ['payment-complete'],
  computed: {
    isEurope() {
      return EUROPEAN_COUNTRY_CODES.includes(this.countrySelected.toUpperCase());
    },
    currencyCode() {
      if (this.isEurope) return 'EUR';
      return { CA: 'CAD', AU: 'AUD', NZ: 'NZD', ZA: 'ZAR' }[this.countrySelected] ?? 'USD';
    },
    currencySymbol() {
      if (this.isEurope) return '€';
      return { CA: 'CA $', AU: 'AU $', NZ: 'NZ $', ZA: 'R' }[this.countrySelected] ?? 'US $';
    },
    amount() {
      let amount = 55000; // 550 USD
      if (this.isEurope) {
        amount = 50000; // 500 EUR
      } else if (this.countrySelected === 'AU' || this.countrySelected === 'CA') {
        amount = 80000; // 800 CAD
      } else if (this.countrySelected === 'ZA') {
        amount = 1000000; // 10000 R
      } else if (this.countrySelected === 'NZ') {
        amount = 90000; // 900 $
      }
      return amount;
    }
  },
  data() {
    return {
      isStripeLoaded: false,
      isCountrySelected: false,
      isPayLoading: false,
      countrySelected: 'US',
      countryList: [
        { name: 'Canada', code: 'CA', image: require('@/assets/canada.png') },
        { name: 'Ireland/Europe', code: 'IE', image: require('@/assets/europe.png') },
        { name: 'Australia', code: 'AU', image: require('@/assets/australia.png') },
        { name: 'USA/Rest of World', code: 'US', image: require('@/assets/usa.png') },
        { name: 'South Africa', code: 'ZA', image: require('@/assets/south-africa.png') },
        { name: 'New Zealand', code: 'NZ', image: require('@/assets/new-zealand.png') }
      ],
      stripe: null,
      cardElement: null,
      addressElement: null,
      fullName: null,
      firstName: null,
      lastName: null,
      shippingAddress: null,
      phone: null,
      email: '',
      howDidYouHearAboutUs: '',
      agent: '',
      stripeValidationError: '',
      isAgreeTermsConditions: false,
      formValid: false,
      formRules: {
        email: [(v) => !!v || 'Email required', (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
        terms: [(v) => !!v || 'Terms required']
      }
    };
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    async handleSelectCountry(country) {
      this.isCountrySelected = true;
      this.countrySelected = country.code;
      try {
        // eslint-disable-next-line no-undef
        this.stripe = await Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        this.isStripeLoaded = true;
        await this.$nextTick();
        this.mountElements();
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    formatAmount(amount) {
      return amount / 100;
    },
    mountElements() {
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', { hidePostalCode: true });
      this.cardElement.mount('#card-element');
      this.addressElement = elements.create('address', {
        mode: 'billing',
        fields: { phone: 'always', name: 'auto' },
        display: { name: 'split' },
        validation: { phone: { required: 'always' } },
        defaultValues: { address: { country: this.countrySelected } }
      });
      this.addressElement.mount('#address-element');
      this.cardElement.on('change', (event) => {
        this.stripeValidationError = event.error ? event.error.message : '';
      });
      this.addressElement.on('change', this.setValidationError);
    },
    setValidationError(event) {
      const { error, address, name, firstName, lastName, phone } = event.value;
      this.stripeValidationError = error ? error?.message : '';
      if (event.complete) {
        this.shippingAddress = address;
        this.fullName = name;
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
      } else {
        this.shippingAddress = null;
        this.fullName = null;
        this.firstName = null;
        this.lastName = null;
        this.phone = null;
      }
      if (address?.country) {
        this.countrySelected = address.country;
      }
    },
    async pay() {
      if (this.formValid !== true) return;
      if (!(this.fullName && this.phone && this.shippingAddress && this.email)) {
        this.stripeValidationError = 'Fill in your personal data!';
        return;
      }
      this.isPayLoading = true;
      try {
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.fullName,
            email: this.email,
            phone: this.phone,
            address: this.shippingAddress
          }
        });
        if (error) {
          this.stripeValidationError = error?.message ?? '';
          return;
        }
        const response = await StripeService.sendFormData({
          payment_method_id: paymentMethod.id,
          customerId: null,
          currency: this.currencyCode,
          amount: this.amount,
          email: this.email,
          setup_future_usage: false,
          shiping_details: {
            name: this.fullName,
            phone: this.phone,
            address: this.shippingAddress
          },
          howDidYouHearAboutUs: this.howDidYouHearAboutUs,
          agent: this.agent,
          description: 'Pension Keeper - Application and Reapply'
        });
        await this.handleServerResponse(response);
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isPayLoading = false;
      }
    },
    async handleServerResponse(response) {
      const { error, requires_action, payment_intent_client_secret } = response;
      if (error) {
        this.showSnackbar({ text: typeof error === 'string' ? error : 'Payment failed', color: 'red' });
      } else if (requires_action) {
        const { error: errorAction, paymentIntent } = await this.stripe.handleCardAction(payment_intent_client_secret);
        if (errorAction) {
          this.stripeValidationError = errorAction.message ?? '';
          return;
        }
        const serverResponse = await StripeService.sendFormData({ payment_intent_id: paymentIntent.id });
        await this.handleServerResponse(serverResponse);
      } else if (response.status === 'Success' && response.id) {
        await this.createApplication(response);
        this.$emit('payment-complete', response.id);
      }
    },
    async createApplication({ id, paymentMethod, customer, latest_charge }) {
      try {
        const { status } = await applicationsService.createReapplyApplicationRecords({
          id,
          amount: this.amount,
          currencyCode: this.currencyCode,
          email: this.email,
          name: this.fullName,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          address: {
            street: this.shippingAddress.line1 + (this.shippingAddress.line2 ? ', ' + this.shippingAddress.line2 : ''),
            postalCode: this.shippingAddress.postal_code ?? '',
            state: this.shippingAddress.state ?? '',
            city: this.shippingAddress.city ?? '',
            country: this.shippingAddress.country ?? ''
          },
          paymentMethod,
          customer: customer ?? null,
          latest_charge,
          howDidYouHearAboutUs: this.howDidYouHearAboutUs,
          agent: this.agent
        });
        if (status !== 'success') {
          this.showSnackbar({ text: 'Payment succeeded, but the application was not created', color: 'red' });
        }
      } catch (err) {
        console.error(err);
        this.showSnackbar({
          text: err?.message || 'Payment succeeded, but the application was not created',
          color: 'red'
        });
      }
    }
  }
};
</script>
